/**
 * Packaging Utils — централизованная логика управления упаковкой
 * Tiefkühlverpackung (frozen) и Kühlverpackung (cooled)
 *
 * Правила:
 * - product_type === 'frozen' → добавить Tiefkühlverpackung (1 шт)
 * - product_type === 'cooled' → добавить Kühlverpackung (1 шт)
 * - product_type === 'fresh' или другое → ничего
 * - Только цифровые товары (requires_shipping=false) → ничего
 * - Обе упаковки могут быть одновременно
 */
window.PackagingUtils = (() => {
  // Глобальный кеш корзины (3с TTL)
  let _cartCache = null;
  let _cartCacheTime = 0;
  const CACHE_TTL = 3000;

  // Флаг для предотвращения параллельных ensurePackaging
  let _isProcessing = false;

  // Debounce timer
  let _debounceTimer = null;

  /**
   * Читаем ID упаковок из скрытого input .additional-product в theme.liquid
   */
  function getConfig() {
    const el = document.querySelector('.additional-product');
    if (!el) return null;
    return {
      frozenId: el.dataset.product || '',
      cooledId: el.dataset.productCooled || '',
    };
  }

  /**
   * Получить полную корзину (с кешем)
   */
  async function fetchFullCart(forceRefresh = false) {
    const now = Date.now();
    if (!forceRefresh && _cartCache && (now - _cartCacheTime) < CACHE_TTL) {
      return _cartCache;
    }
    const res = await fetch(`${routes.cart_url}.js`, {
      headers: { 'Accept': 'application/json' },
    });
    const cart = await res.json();
    _cartCache = cart;
    _cartCacheTime = Date.now();
    return cart;
  }

  /**
   * Сбросить кеш (вызывать после изменения корзины)
   */
  function invalidateCache() {
    _cartCache = null;
    _cartCacheTime = 0;
  }

  /**
   * Обновить кеш, если данные — полная корзина
   */
  function updateCache(cartData) {
    if (cartData && cartData.item_count !== undefined && Array.isArray(cartData.items)) {
      _cartCache = cartData;
      _cartCacheTime = Date.now();
    }
  }

  /**
   * Определение типа товара — строго по product_type
   */
  function isFrozenType(productType) {
    return (productType || '').toLowerCase().trim() === 'frozen';
  }

  function isCooledType(productType) {
    return (productType || '').toLowerCase().trim() === 'cooled';
  }

  function getVariantId(item) {
    return String(item.variant_id != null ? item.variant_id : item.id);
  }

  /**
   * Анализ корзины: какие упаковки нужны, какие уже есть
   * Возвращает { toAdd: string[], toRemove: string[] }
   */
  function getPackagingDiff(cart, frozenId, cooledId) {
    const items = cart.items || [];
    const fId = String(frozenId || '');
    const cId = String(cooledId || '');

    // Отделяем «обычные» товары от упаковки (id в cart.js — variant_id)
    const otherItems = items.filter(
      (it) => getVariantId(it) !== fId && getVariantId(it) !== cId
    );

    // Если корзина содержит только цифровые товары — упаковка не нужна
    const hasPhysicalItems = otherItems.some((it) => it.requires_shipping);
    if (otherItems.length > 0 && !hasPhysicalItems) {
      const toRemove = [];
      if (fId && items.some((it) => getVariantId(it) === fId)) toRemove.push(fId);
      if (cId && items.some((it) => getVariantId(it) === cId)) toRemove.push(cId);
      return { toAdd: [], toRemove };
    }

    const needsFrozen = otherItems.some((it) => isFrozenType(it.product_type));
    const needsCooled = otherItems.some((it) => isCooledType(it.product_type));

    const frozenExists = fId ? items.some((it) => getVariantId(it) === fId) : false;
    const cooledExists = cId ? items.some((it) => getVariantId(it) === cId) : false;

    const toAdd = [];
    const toRemove = [];

    // Frozen packaging
    if (fId) {
      if (needsFrozen && !frozenExists) toAdd.push(fId);
      if (!needsFrozen && frozenExists) toRemove.push(fId);
    }

    // Cooled packaging
    if (cId) {
      if (needsCooled && !cooledExists) toAdd.push(cId);
      if (!needsCooled && cooledExists) toRemove.push(cId);
    }

    // Enforce quantity = 1
    const frozenItem = fId ? items.find((it) => getVariantId(it) === fId) : null;
    const cooledItem = cId ? items.find((it) => getVariantId(it) === cId) : null;
    const quantityFixes = {};
    if (frozenItem && frozenItem.quantity > 1) quantityFixes[fId] = 1;
    if (cooledItem && cooledItem.quantity > 1) quantityFixes[cId] = 1;

    return { toAdd, toRemove, quantityFixes };
  }

  /**
   * Определить, какие упаковки нужно добавить в items[] запроса при добавлении товара
   * (проактивное добавление — в тот же запрос)
   *
   * @param {Object} cart - текущая корзина (может быть null)
   * @param {string} frozenId - ID варианта Tiefkühlverpackung
   * @param {string} cooledId - ID варианта Kühlverpackung
   * @param {string} newProductType - product_type добавляемого товара
   * @param {boolean} newProductRequiresShipping - requires_shipping добавляемого товара
   * @returns {string[]} - ID упаковок для добавления
   */
  function getPackagingItemsForAdd(cart, frozenId, cooledId, newProductType, newProductRequiresShipping = true) {
    const items = cart ? (cart.items || []) : [];

    // Если добавляемый товар — цифровой, и в корзине нет физических
    if (!newProductRequiresShipping) {
      const hasPhysical = items.some(
        (it) => it.requires_shipping && String(it.id) !== String(frozenId) && String(it.id) !== String(cooledId)
      );
      if (!hasPhysical) return [];
    }

    // Упаковки уже в корзине?
    const frozenExists = frozenId ? items.some((it) => getVariantId(it) === String(frozenId)) : false;
    const cooledExists = cooledId ? items.some((it) => getVariantId(it) === String(cooledId)) : false;

    // Есть ли уже frozen/cooled товары в корзине (кроме упаковок)?
    const otherItems = items.filter(
      (it) => getVariantId(it) !== String(frozenId) && getVariantId(it) !== String(cooledId)
    );
    const hasFrozenInCart = otherItems.some((it) => isFrozenType(it.product_type));
    const hasCooledInCart = otherItems.some((it) => isCooledType(it.product_type));

    const isAddingFrozen = isFrozenType(newProductType);
    const isAddingCooled = isCooledType(newProductType);

    const result = [];

    if (frozenId && !frozenExists && (isAddingFrozen || hasFrozenInCart)) {
      result.push(frozenId);
    }
    if (cooledId && !cooledExists && (isAddingCooled || hasCooledInCart)) {
      result.push(cooledId);
    }

    return result;
  }

  /**
   * Применить изменения упаковки к корзине (реактивная коррекция)
   * Используется из cart.js / cart-drawer.js после обновления корзины
   */
  async function applyPackagingChanges(diff, sectionsToRender) {
    const sections = sectionsToRender || [];
    let lastResult = null;

    // Исправление количества (enforce qty=1)
    if (diff.quantityFixes && Object.keys(diff.quantityFixes).length > 0) {
      const body = JSON.stringify({
        updates: diff.quantityFixes,
        sections: sections,
        sections_url: window.location.pathname,
      });
      await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
      invalidateCache();
    }

    // Удаление лишних упаковок
    if (diff.toRemove && diff.toRemove.length > 0) {
      const updates = {};
      diff.toRemove.forEach((id) => { updates[id] = 0; });
      const body = JSON.stringify({
        updates,
        sections: sections,
        sections_url: window.location.pathname,
      });
      const res = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
      const data = await res.json();
      invalidateCache();
      updateCache(data);
      lastResult = data;
    }

    // Добавление недостающих упаковок (выполняется даже если было удаление)
    if (diff.toAdd && diff.toAdd.length > 0) {
      const addItems = diff.toAdd.map((id) => ({ id: parseInt(id), quantity: 1 }));
      const body = JSON.stringify({ items: addItems, sections: sections });
      const res = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' },
        body,
      });
      const data = await res.json();
      invalidateCache();
      lastResult = data;
    }

    return lastResult;
  }

  /**
   * ensurePackaging — главная функция реактивной проверки
   * Вызывается из cart.js / cart-drawer.js после обновления корзины
   * С debounce 300ms для защиты от множественных вызовов
   *
   * @param {Object|null} cartData - данные корзины (если есть)
   * @param {string[]} sections - секции для перерендера
   * @param {Function|null} onComplete - callback после завершения (получает данные ответа или null)
   */
  function ensurePackaging(cartData, sections, onComplete) {
    if (_debounceTimer) clearTimeout(_debounceTimer);

    // Сбросить кеш перед проверкой, чтобы не использовать устаревшие данные после удаления товара
    invalidateCache();
    if (cartData && cartData.item_count !== undefined && Array.isArray(cartData.items)) {
      updateCache(cartData);
    }

    _debounceTimer = setTimeout(async () => {
      if (_isProcessing) {
        // Предыдущий запуск ещё не завершён — перезапланируем проверку
        ensurePackaging(null, sections, onComplete);
        return;
      }
      _isProcessing = true;

      try {
        const config = getConfig();
        if (!config) {
          _isProcessing = false;
          return;
        }

        invalidateCache();
        const cart = await fetchFullCart(true); // Всегда свежие данные после удаления
        const diff = getPackagingDiff(cart, config.frozenId, config.cooledId);

        const hasChanges =
          (diff.toAdd && diff.toAdd.length > 0) ||
          (diff.toRemove && diff.toRemove.length > 0) ||
          (diff.quantityFixes && Object.keys(diff.quantityFixes).length > 0);

        if (hasChanges) {
          const result = await applyPackagingChanges(diff, sections || []);
          if (onComplete) onComplete(result);
        }
      } catch (e) {
        console.error('PackagingUtils.ensurePackaging error:', e);
      } finally {
        _isProcessing = false;
      }
    }, 300);
  }

  return {
    getConfig,
    fetchFullCart,
    invalidateCache,
    updateCache,
    isFrozenType,
    isCooledType,
    getPackagingDiff,
    getPackagingItemsForAdd,
    applyPackagingChanges,
    ensurePackaging,
  };
})();
